import { useCallback, useEffect, useMemo, useState } from "react";

import { Column, OutlinedChip, Row, SearchInput, Text } from "@bridgesplit/ui";
import { TokenListMetadata, TokenListTag, UI_TOKEN_LIST_TAGS, UiTokenListTag } from "@bridgesplit/abf-sdk";
import { EMPTY_STRING, textContains } from "@bridgesplit/utils";
import { Collapse } from "@mui/material";

import { TokenListFilterBadge } from "./TokenListFilterBadge";

type TokenListSearchState = {
    search: string;
    selectedTags: UiTokenListTag[];
    open: boolean;
};

export type UseTokenListFiltersProps<T> = {
    rows: T[];
    getMetadata: (row: T) => TokenListMetadata | undefined;
    /** Ref to register reset handler – clears all active filters when called */
    resetRef?: React.MutableRefObject<(() => void) | null>;
};

export type TokenListSearchProps = {
    search: string;
    selectedTags: UiTokenListTag[];
    open: boolean;
    availableTags: UiTokenListTag[];
    setSearch: (search: string) => void;
    toggleTag: (tag: UiTokenListTag) => void;
    setOpen: (open: boolean) => void;
};

function matchesSearch(metadata: TokenListMetadata, search: string): boolean {
    if (!search.trim()) return true;
    return textContains([metadata.symbol, metadata.name, metadata.mint], search);
}

function matchesTags(metadata: TokenListMetadata, selectedTags: UiTokenListTag[]): boolean {
    if (selectedTags.length === 0) return true;
    const rowTags = metadata.tags ?? [];
    return selectedTags.some((tag) => rowTags.includes(tag));
}

export function filterTokenListRows<T>({
    rows,
    getMetadata,
    state
}: {
    rows: T[];
    getMetadata: (row: T) => TokenListMetadata | undefined;
    state: Pick<TokenListSearchState, "search" | "selectedTags">;
}) {
    const hasActiveFilters = !!state.search.trim() || state.selectedTags.length > 0;
    return rows.filter((row) => {
        const meta = getMetadata(row);
        if (!meta) {
            return !hasActiveFilters;
        }
        if (!matchesSearch(meta, state.search)) return false;
        if (!matchesTags(meta, state.selectedTags)) return false;
        return true;
    });
}

export function useTokenListFilters<T>({
    rows,
    getMetadata,
    resetRef
}: UseTokenListFiltersProps<T>): TokenListSearchProps & { filteredRows: T[] } {
    const [search, setSearch] = useState(EMPTY_STRING);
    const [selectedTags, setSelectedTags] = useState<UiTokenListTag[]>([]);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        if (!resetRef) return;
        resetRef.current = () => {
            setSearch(EMPTY_STRING);
            setSelectedTags([]);
            setOpen(false);
        };
        return () => {
            resetRef.current = null;
        };
    }, [resetRef]);

    const availableTags = useMemo(() => {
        const tagSet = new Set<TokenListTag>();
        for (const row of rows) {
            const meta = getMetadata(row);
            (meta?.tags ?? []).forEach((t) => tagSet.add(t));
        }
        return UI_TOKEN_LIST_TAGS.filter((t) => tagSet.has(t));
    }, [rows, getMetadata]);

    const filteredRows = useMemo(
        () => filterTokenListRows({ rows, getMetadata, state: { search, selectedTags } }),
        [rows, getMetadata, search, selectedTags]
    );

    const toggleTag = useCallback((tag: UiTokenListTag) => {
        setSelectedTags((prev) => (prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]));
    }, []);

    return {
        search,
        selectedTags,
        open,
        availableTags,
        setSearch,
        toggleTag,
        setOpen,
        filteredRows
    };
}

export function TokenListSearch({
    search,
    selectedTags,
    open,
    availableTags,
    setSearch,
    toggleTag,
    setOpen
}: TokenListSearchProps) {
    const activeFilterCount = (search.trim() ? 1 : 0) + selectedTags.length;
    const hasFiltersAvailable = availableTags.length > 0;

    return (
        <Column spacing={1}>
            <Row spacing={2}>
                <SearchInput
                    size="small"
                    placeholder="Search by symbol, name, or mint"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    fullWidth
                />
                <TokenListFilterBadge
                    activeCount={activeFilterCount}
                    hasFiltersAvailable={hasFiltersAvailable}
                    onClick={() => setOpen(!open)}
                />
            </Row>
            <Collapse in={open}>
                {availableTags.length > 0 ? (
                    <Row spacing={0.5} sx={{ flexWrap: "wrap", gap: 0.5 }}>
                        {availableTags.map((tag) => (
                            <OutlinedChip
                                key={tag}
                                isSelected={selectedTags.includes(tag)}
                                onClick={() => toggleTag(tag)}
                                sx={{ flexShrink: 0 }}
                            >
                                <Text variant="body2">{tag}</Text>
                            </OutlinedChip>
                        ))}
                    </Row>
                ) : (
                    <Text variant="body2" color="caption" sx={{ py: 0.5 }}>
                        No filter tags available for this list
                    </Text>
                )}
            </Collapse>
        </Column>
    );
}
